import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import JsxParser from "react-jsx-parser";

const initialFormData = {
  status: "",
  comments: "",
  strengths: "",
  developmentAreas: "",
  extensionReason: "",
  areasToImprove: "",
  extendedEndDate: "",
  supportProvided: "",
  nextReviewDate: "",
  terminationReason: "",
  evidence: "",
  feedbackGiven: "",
  finalReviewDate: "",
  hrSignoff: "",
  bookMeeting: "",
};

const terminationReasons = [
  "Capability",
  "Conduct",
  "Attendance",
  "Failure to meet objectives",
];

const ProbationForm = ({ staffId, staffName, role, location, type }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [code, setCode] = useState(null);
  const [meeting, setMeeting] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  useEffect(() => {
    if (!formData.finalReviewDate) {
      setMeeting(null);
      return;
    }
    const getMeetings = async () => {
      try {
        const response = await fetch(
          `https://api.insightos.co.uk/api/meetings/${formData.finalReviewDate}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!response.ok) throw new Error("Failed to fetch meetings.");
        const data = await response.json();
        if (data.success) setMeeting(data.data);
        else setErrorMessage(data.message || "Failed to fetch meetings.");
      } catch (error) {
        setErrorMessage(error.message || "Something went wrong.");
      }
    };
    getMeetings();
  }, [formData.finalReviewDate]);

 const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setSuccessMessage("");
  setErrorMessage("");

  const data = {
    StaffName: staffName,
    StaffId: staffId,
    Status: formData.status,
    Comments: formData.comments,
    Strengths: formData.strengths,
    DevelopmentAreas: formData.developmentAreas,
    ExtensionReason: formData.extensionReason,
    AreasToImprove: formData.areasToImprove,
    ExtendedEndDate: formData.extendedEndDate,
    SupportProvided: formData.supportProvided,
    NextReviewDate: formData.nextReviewDate,
    TerminationReason: formData.terminationReason,
    Evidence: formData.evidence,
    FeedbackGiven: formData.feedbackGiven,
    FinalReviewDate: formData.finalReviewDate,
    HrSignoff: formData.hrSignoff,
    BookMeeting: formData.bookMeeting,
  };

  try {
    const response = await fetch(
      "https://api.insightos.co.uk/api/probationform",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) throw new Error("Failed to submit form.");

    const result = await response.json();
    if (result.success) {
      if (result.reactCode) setCode(result.reactCode);
      setSuccessMessage(result.message || "Submitted successfully. Please check your emails.");
    } else {
      setErrorMessage(result.message || "Submission failed.");
    }
  } catch (error) {
    setErrorMessage(error.message || "Something went wrong.");
  } finally {
    setLoading(false);
  }
};


  return (
    <div className="max-w-3xl mx-auto p-8 bg-white shadow-2xl rounded-2xl mt-12 border border-gray-100">
      <h1 className="text-3xl font-extrabold mb-6 text-blue-900 tracking-tight">
        Probation Review Form
      </h1>

      {successMessage && (
        <div className="mb-4 p-3 rounded bg-green-50 border border-green-200 text-green-800 font-medium">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-800 font-medium">
          {errorMessage}
        </div>
      )}
      <h2 className="text-xl font-semibold mb-4 text-blue-800">
        {staffName} ({role}) - {type}
      </h2>
      {code && (
        <div className="bg-gray-50 p-4 rounded-lg mb-4 mt-4 border border-gray-200">
          <JsxParser jsx={code} components={{}} />
        </div>
      )}
      <div id="probation-form-content">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Probation Status */}
          <div>
            <span className="block text-blue-900 font-semibold mb-2">
              Probation Status
            </span>
            <div className="flex gap-6">
              {["Pass", "Fail", "Extend"].map((status) => (
                <label key={status} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="status"
                    value={status}
                    checked={formData.status === status}
                    onChange={handleChange}
                    className="accent-blue-600"
                  />
                  {status}
                </label>
              ))}
            </div>
          </div>
          {/* Pass Section */}
          {formData.status === "Pass" && (
            <>
              <div>
                <label
                  htmlFor="comments"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Manager's Comments
                </label>
                <textarea
                  id="comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                ></textarea>
              </div>
              <div>
                <label
                  htmlFor="strengths"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Summary of Strengths (optional)
                </label>
                <textarea
                  id="strengths"
                  name="strengths"
                  value={formData.strengths}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                ></textarea>
              </div>
              <div>
                <label
                  htmlFor="developmentAreas"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Recommended Development Areas (optional)
                </label>
                <textarea
                  id="developmentAreas"
                  name="developmentAreas"
                  value={formData.developmentAreas}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                ></textarea>
              </div>
            </>
          )}
          {/* Extend Section */}
          {formData.status === "Extend" && (
            <>
              <div>
                <label
                  htmlFor="extensionReason"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Reason for Extension
                </label>
                <input
                  type="text"
                  id="extensionReason"
                  name="extensionReason"
                  value={formData.extensionReason}
                  onChange={handleChange}
                  required
                  placeholder="e.g., attendance, performance, behaviour"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label
                  htmlFor="areasToImprove"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Specific Areas to Improve
                </label>
                <textarea
                  id="areasToImprove"
                  name="areasToImprove"
                  value={formData.areasToImprove}
                  onChange={handleChange}
                  required
                  rows="2"
                  placeholder="Checklist or open field"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                ></textarea>
              </div>
              <div>
                <label
                  htmlFor="extendedEndDate"
                  className="block text-blue-900 font-medium mb-2"
                >
                  New End Date
                </label>
                <input
                  type="date"
                  id="extendedEndDate"
                  name="extendedEndDate"
                  value={formData.extendedEndDate}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label
                  htmlFor="supportProvided"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Support Provided
                </label>
                <input
                  type="text"
                  id="supportProvided"
                  name="supportProvided"
                  value={formData.supportProvided}
                  onChange={handleChange}
                  required
                  placeholder="e.g., mentoring, additional training, workload changes"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label
                  htmlFor="finalReviewDate"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Date of Meeting
                </label>
                <input
                  type="date"
                  id="finalReviewDate"
                  name="finalReviewDate"
                  value={formData.finalReviewDate}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              {meeting && meeting.length > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg mt-4 border border-blue-100">
                  <h3 className="text-lg font-semibold mb-2 text-blue-900">
                    Meetings on {formData.finalReviewDate}:
                  </h3>
                  <ul className="list-disc pl-5 mb-3">
                    {meeting.map((meet, index) => (
                      <li key={index} className="text-blue-800">
                        {meet.Name} - {meet.Time} - {meet.FinishTime}
                      </li>
                    ))}

                  </ul>

                  <label
                    htmlFor="bookMeeting"
                    className="block text-blue-900 font-medium mb-2"
                  >
                    Book Meeting Time
                  </label>
                  <input
                    id="bookMeeting"
                    name="bookMeeting"
                    type="time"
                    value={formData.bookMeeting}
                    onChange={handleChange}
                    className="block w-full rounded-lg bg-white px-3 py-2 border border-gray-300 text-base text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    (Meeting will be booked and saved automatically on submit.)
                  </p>
                </div>
              )}
              <div>
                <label
                  htmlFor="nextReviewDate"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Next Review Date
                </label>
                <input
                  type="date"
                  id="nextReviewDate"
                  name="nextReviewDate"
                  value={formData.nextReviewDate}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>


            </>
          )}
          {/* Fail Section */}
          {formData.status === "Fail" && (
            <>
              <div>
                <label
                  htmlFor="terminationReason"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Reason for Termination
                </label>
                <select
                  id="terminationReason"
                  name="terminationReason"
                  value={formData.terminationReason}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">Select reason</option>
                  {terminationReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="evidence"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Evidence or Examples
                </label>
                <textarea
                  id="evidence"
                  name="evidence"
                  value={formData.evidence}
                  onChange={handleChange}
                  required
                  rows="3"
                  placeholder="e.g., missed deadlines, complaints, disciplinary notes"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                ></textarea>
              </div>
              <div>
                <span className="block text-blue-900 font-medium mb-2">
                  Was Performance Feedback Given During Probation?
                </span>
                <div className="flex gap-6">
                  {["Yes", "No"].map((val) => (
                    <label key={val} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="feedbackGiven"
                        value={val}
                        checked={formData.feedbackGiven === val}
                        onChange={handleChange}
                        required
                        className="accent-blue-600"
                      />
                      {val}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label
                  htmlFor="finalReviewDate"
                  className="block text-blue-900 font-medium mb-2"
                >
                  Date of Final Review Meeting
                </label>
                <input
                  type="date"
                  id="finalReviewDate"
                  name="finalReviewDate"
                  value={formData.finalReviewDate}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              {meeting && meeting.length > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg mt-4 border border-blue-100">
                  <h3 className="text-lg font-semibold mb-2 text-blue-900">
                    Meetings on {formData.finalReviewDate}:
                  </h3>
                  <ul className="list-disc pl-5 mb-3">
                    {meeting.map((meet, index) => (
                      <li key={index} className="text-blue-800">
                        {meet.Name} - {meet.Time} - {meet.FinishTime}
                      </li>
                    ))}
                  </ul>
                  <label
                    htmlFor="bookMeeting"
                    className="block text-blue-900 font-medium mb-2"
                  >
                    Book Meeting Time
                  </label>
                  <input
                    id="bookMeeting"
                    name="bookMeeting"
                    type="time"
                    value={formData.bookMeeting}
                    onChange={handleChange}
                    className="block w-full rounded-lg bg-white px-3 py-2 border border-gray-300 text-base text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  
                </div>
              )}
              <div>
                <label
                  htmlFor="hrSignoff"
                  className="block text-blue-900 font-medium mb-2"
                >
                  HR Sign-off (optional)
                </label>
                <input
                  type="text"
                  id="hrSignoff"
                  name="hrSignoff"
                  value={formData.hrSignoff}
                  onChange={handleChange}
                  placeholder="HR name or initials"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-2 rounded">
                <p className="text-yellow-800 text-sm">
                  <strong>Legal Guidance:</strong> Ensure that the reason for probation failure is clearly communicated and documented to reduce legal exposure.
                </p>
              </div>
            </>
          )}
          {/* Submit Button */}
          <div>
            <button
              type="submit"
              data-component="probation-form"
              data-time-saved="60"
              data-feature="submit_probation_review_post"
              className={`w-full px-4 py-2 rounded-lg text-white font-semibold shadow transition ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-700 hover:bg-blue-800"
              }`}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProbationForm;
